<template>
  <layout-root>
    <NuxtLoadingIndicator color="#7acee0" />
    <header-banner v-if="banner && !hideFooter" :banner="banner" />
    <common-header v-if="!hideHeaderFooter" />
    <common-to-top v-if="!$store.event_search_modal" />
    <slot />
    <footer-tab v-if="$store.isSP && canDisplayFooterTab" />
    <DelayHydration v-if="!hideFooter && !hideBottomLink">
      <common-bottom-links />
    </DelayHydration>
    <machicon-description v-if="!hideHeaderFooter && !hideFooter" />
    <DelayHydration v-if="!hideHeaderFooter && !hideFooter">
      <common-footer />
    </DelayHydration>
  </layout-root>
</template>

<script>
import { timeConverter } from '@/utils/converter'
const LayoutRoot = defineAsyncComponent(() => import('@/components/LayoutRoot.vue'))
const CommonHeader = defineAsyncComponent(() => import('@/components/header/CommonHeader.vue'))
const CommonToTop = defineAsyncComponent(() => import('@/components/CommonToTop.vue'))
const CommonFooter = defineAsyncComponent(() => import('@/components/CommonFooter.vue'))
const CommonBottomLinks = defineAsyncComponent(() => import('@/components/CommonBottomLinks.vue'))
const NotificationMessage = defineAsyncComponent(() => import('@/components/NotificationMessage.vue'))
const MachiconDescription = defineAsyncComponent(() => import('@/components/MachiconDescription.vue'))
const HeaderBanner = defineAsyncComponent(() => import('@/components/header/HeaderBanner.vue'))
import PageDetector from '@/utils/PageDetector'
const FooterTab = defineAsyncComponent(() => import('@/components/FooterTab.vue'))
import FirebaseConnect from '@/utils/FirebaseConnect'
import { collection, getDocs, onSnapshot, addDoc } from 'firebase/firestore'
const NATIVE_APP_HIDE_PAGES = ['/terms_of_service', '/privacy', '/tokushou']
const DEFAULT_ALT = 'machicon'
const IPHONE_X_SERIES_SCREEN_PIXEL_LIST = [
  // width * height * pixel_ratio
  375 * 812 * 3, // iPhoneX or Xs
  414 * 896 * 3, // iPhoneXsMax
  414 * 896 * 2 // iPhoneXR
]
export default {
  components: {
    LayoutRoot,
    CommonHeader,
    CommonFooter,
    CommonBottomLinks,
    MachiconDescription,
    NotificationMessage,
    HeaderBanner,
    FooterTab,
    CommonToTop
  },
  data() {
    return {
      currentViewportWidth: null,
      currentCategory: ''
    }
  },
  computed: {
    pageDetector() {
      const route = useRoute()
      return new PageDetector(route)
    },
    hideHeaderFooter() {
      const route = useRoute()
      if (this.pageDetector.isMessageDetailsPage) return true
      if (!this.$store.$state.native_app) return false
      return NATIVE_APP_HIDE_PAGES.includes(route.path.replace(/\/$/, ''))
    },
    hideFooter() {
      return this.pageDetector.isParticipantsPage || this.pageDetector.isMarriagesPage || this.pageDetector.isClEventsPage
    },
    hideBottomLink() {
      return this.pageDetector.isMessageDetailsPage
    },
    bannerSetting() {
      return this.$store.$state.layout_data.banner_header.data
    },
    canDisplayBanner() {
      return this.pageDetector.isTopPage && this.bannerSetting && this.isDisplayBannerTime
    },
    canDisplayFooterTab() {
      if (this.$store.$state.native_app) return false
      if (this.pageDetector.isEventListPage || this.pageDetector.isTopPage) return true
      if (this.pageDetector.isGenderOrCompleteParticipantPage || this.pageDetector.isParticipantsListPage) return true
      if (this.pageDetector.isMarriagesPage) return true
      if (this.pageDetector.isClEventsPage) return true
      if (this.pageDetector.isMessagesPage) return true
    },
    isDisplayBannerTime() {
      const now = timeConverter().dateTime
      const startAt = this.bannerSetting.display_start_at
      const endAt = this.bannerSetting.display_end_at

      if (startAt && endAt) return now.isBetween(startAt, endAt, null, '[]')
      if (startAt) return now.isAfter(startAt)
      if (endAt) return now.isBefore(endAt)

      return true
    },
    banner() {
      if (this.canDisplayBanner) {
        return {
          webpPcImage: this.bannerSetting.pc_image_webp,
          webpSpImage: this.bannerSetting.sp_image_webp,
          pcImage: this.bannerSetting.pc_image,
          spImage: this.bannerSetting.sp_image,
          url: this.bannerSetting.url,
          alt: this.bannerSetting.alt || DEFAULT_ALT
        }
      }
      return null
    },
    userFirebaseUidDefault() {
      return this.$store.$state.user_firebase_id
    }
  },
  watch: {
    userFirebaseUidDefault() {
      this.checkNewMessage()
    }
  },
  mounted() {
    window.addEventListener('resize', this.onResize, { passive: true })
    this.onResize()
    if (this.$store.isLoggedIn) {
      FirebaseConnect.signInFirebase(this)
    }
  },
  unmounted() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onResize() {
      this.setFillHeight()
      if (!this.$store.$state.ios) return
      const currentDevicePixel = window.screen.width * window.screen.height * window.devicePixelRatio
      if (IPHONE_X_SERIES_SCREEN_PIXEL_LIST.some(pixel => pixel === currentDevicePixel)) {
        this.$store.SET_STATE({ key: 'iphone_x_series', value: true })
      } else {
        this.$store.SET_STATE({ key: 'iphone_x_series', value: false })
      }
    },
    setFillHeight() {
      const vh = window.innerHeight
      document.documentElement.style.setProperty('--real-vh', `${vh}px`)
    },
    async checkNewMessage() {
      try {
        const colectionRef = collection(FirebaseConnect.db(), `user_${this.$store.$state.my_profile.id}`)
        const docSnap = await getDocs(colectionRef)
        if (docSnap.empty) {
          await addDoc(colectionRef, {
            count: 0,
            author_id: this.userFirebaseUidDefault
          })
        }
        const unsubscribe = await onSnapshot(colectionRef, querySnapshot => {
          querySnapshot.forEach(doc => {
            const countMessage = doc.data()['count'] || 0
            this.$store.SET_STATE({ key: 'has_new_message', value: doc.data()['count'] > 0 })
            this.$store.SET_STATE({ key: 'count_message', value: countMessage })
          })
        })
        if (window !== undefined) {
          window.addEventListener(
            'beforeunload',
            function (e) {
              unsubscribe()
            },
            { passive: true }
          )
        }
      } catch (e) {}
    }
  }
}
</script>

<style lang="scss" scoped>
.loading-margin {
  margin: 1em auto;
}
</style>
